import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, ArrowUpRight, BookOpen, ChevronDown, ChevronUp, FlaskConical, Link2, X,
} from 'lucide-react';
import { Highlight } from './Highlight';
import { labLine } from './ResultRow';
import { Badge } from '../ui/Badge';
import { Button, IconButton } from '../ui/Button';
import { CopyButton, Notice, Spinner } from '../ui/misc';
import { useResultDetails } from '../../hooks/useResultDetails';
import { formatKey, orderedSections, valueToText } from '../../lib/resultModel';
import { sanitizeHtml } from '../../lib/html';
import { looksLikeHtml } from '../../lib/text';
import { cx } from '../../lib/cx';

const KEY_FACTS = [
  ['specimen', 'Specimen'],
  ['container', 'Container'],
  ['volume', 'Volume'],
  ['stability', 'Stability & storage'],
  ['transport', 'Transport'],
  ['turnaround', 'Turnaround'],
  ['frequency', 'How often'],
  ['performedAt', 'Laboratory'],
  ['testCode', 'Test code'],
  ['referredTo', 'Referred to'],
];

const IMAGE_URL_RE = /^https?:\/\/\S+\.(jpe?g|gif|png|webp)(\?\S*)?$/i;

const isDocLink = (item) => item && typeof item === 'object' && item.href && item.label;

/** Renders any detail value: rich text, lists, document links, images, nested objects. */
const Value = ({ data, highlighter }) => {
  if (data === null || data === undefined || data === '') return <span className="text-faint">Not stated</span>;

  if (Array.isArray(data)) {
    if (data.every(isDocLink)) {
      return (
        <ul className="space-y-1">
          {data.map((doc, i) => (
            <li key={i}>
              {/^https?:/i.test(doc.href) ? (
                <a
                  href={doc.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  referrerPolicy="no-referrer"
                  className="inline-flex items-center gap-1 text-accent-fg underline underline-offset-4"
                >
                  {valueToText(doc.label)} <ArrowUpRight size={13} aria-hidden="true" />
                </a>
              ) : valueToText(doc.label)}
            </li>
          ))}
        </ul>
      );
    }
    return (
      <ul className="list-disc space-y-0.5 pl-5">
        {data.map((item, i) => <li key={i}><Value data={item} highlighter={highlighter} /></li>)}
      </ul>
    );
  }

  if (typeof data === 'object') {
    if (data.text && data.imageUrl) {
      return (
        <span className="flex items-center gap-3">
          <Highlight text={valueToText(data.text)} highlighter={highlighter} />
          {/^https?:/i.test(data.imageUrl) && (
            <img
              src={data.imageUrl}
              alt={`Container: ${valueToText(data.text)}`}
              loading="lazy"
              referrerPolicy="no-referrer"
              className="h-12 w-12 rounded-md border border-line object-cover"
            />
          )}
        </span>
      );
    }
    return (
      <dl className="space-y-1.5 border-l-2 border-accent/25 pl-3">
        {Object.entries(data).map(([k, v]) => (
          <div key={k}>
            <dt className="text-xs text-muted">{formatKey(k)}</dt>
            <dd><Value data={v} highlighter={highlighter} /></dd>
          </div>
        ))}
      </dl>
    );
  }

  const text = String(data);
  const parts = text.split(',').map((s) => s.trim());
  if (parts.length && parts.every((p) => IMAGE_URL_RE.test(p))) {
    return (
      <span className="flex flex-wrap gap-1.5">
        {parts.map((url, i) => (
          <img key={i} src={url} alt={`Specimen tube ${i + 1}`} loading="lazy" referrerPolicy="no-referrer" className="h-12 w-12 rounded-md border border-line object-cover" />
        ))}
      </span>
    );
  }
  if (looksLikeHtml(text)) {
    return (
      <div
        className="rich-text"
        // Sanitised by DOMPurify (and by the backend) before rendering.
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(text, highlighter) }}
      />
    );
  }
  return <span className="whitespace-pre-line"><Highlight text={text} highlighter={highlighter} /></span>;
};

const Section = ({ title, data, highlighter }) => {
  const [open, setOpen] = useState(true);
  const id = useId();
  const isDocs = Array.isArray(data) && data.every(isDocLink);
  return (
    <section className="border-t border-line">
      <h3>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={id}
          className="flex w-full items-center justify-between gap-2 px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-faint hover:text-fg"
        >
          {title}
          <ChevronDown size={14} className={cx('transition-transform', !open && '-rotate-90')} aria-hidden="true" />
        </button>
      </h3>
      <div id={id} hidden={!open} className="px-5 pb-4">
        {isDocs || typeof data !== 'object' || Array.isArray(data) ? (
          <div className="text-sm text-fg"><Value data={data} highlighter={highlighter} /></div>
        ) : (
          <dl className="divide-y divide-line/70">
            {Object.entries(data).map(([key, value]) => {
              if (value === null || value === undefined || value === '') return null;
              const emphasised = key === 'External Laboratory' || key === 'Sending Lab Details';
              // Tables (e.g. SA Pathology's container options) get the full width under their label.
              const wide = typeof value === 'string' && /<table[\s>]/i.test(value);
              return (
                <div
                  key={key}
                  className={cx(
                    'grid gap-x-4 gap-y-0.5 py-2 text-sm',
                    wide ? 'gap-y-1.5' : 'sm:grid-cols-[minmax(8rem,11rem)_1fr]',
                    emphasised && '-mx-2 rounded-md bg-accent/10 px-2',
                  )}
                >
                  <dt className={cx('text-muted', emphasised && 'flex items-center gap-1.5 font-medium text-accent-fg')}>
                    {emphasised && <FlaskConical size={13} aria-hidden="true" />}
                    {formatKey(key)}
                  </dt>
                  <dd className="min-w-0 text-fg"><Value data={value} highlighter={highlighter} /></dd>
                </div>
              );
            })}
          </dl>
        )}
      </div>
    </section>
  );
};

/**
 * Full information for one result, with the key collection facts first,
 * links to the lab, and previous/next.
 */
export const DetailPane = ({
  model, position, total, onClose, onPrev, onNext, highlighter, headingId,
}) => {
  const { details, loading, error, retry } = useResultDetails(model);
  const headingRef = useRef(null);

  const facts = useMemo(() => KEY_FACTS.filter(([key]) => model?.facts[key]), [model]);
  const sections = useMemo(() => orderedSections(details || {}), [details]);

  // j / k step through results while the pane has focus.
  const onKeyDown = (e) => {
    if (e.target.closest('input, textarea, select') || e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.key === 'j' && onNext) { e.preventDefault(); onNext(); }
    if (e.key === 'k' && onPrev) { e.preventDefault(); onPrev(); }
  };

  useEffect(() => {
    // Scroll the pane back to the top when switching results.
    headingRef.current?.closest('[data-detail-scroll]')?.scrollTo({ top: 0 });
  }, [model?.id]);

  if (!model) return null;

  return (
    <article className="flex h-full min-h-0 flex-col bg-surface" aria-labelledby={headingId} onKeyDown={onKeyDown}>
      <header className="border-b border-line px-5 pb-4 pt-3">
        <div className="flex items-center justify-between gap-2">
          <p className="min-w-0 truncate text-xs text-muted">{labLine(model)}</p>
          <div className="-mr-2 flex shrink-0 items-center">
            {total > 1 && (
              <>
                <IconButton icon={ChevronUp} size="sm" label="Previous result (k)" onClick={onPrev} disabled={!onPrev} />
                <span className="px-1 text-xs tabular-nums text-muted" aria-live="polite">
                  {position} <span className="sr-only">of</span><span aria-hidden="true">/</span> {total}
                </span>
                <IconButton icon={ChevronDown} size="sm" label="Next result (j)" onClick={onNext} disabled={!onNext} />
              </>
            )}
            <CopyButton text={window.location.href} label="link to this result" className="ml-1 p-2" />
            <IconButton icon={X} size="sm" label="Close details (Esc)" onClick={onClose} />
          </div>
        </div>
        <h2 id={headingId} ref={headingRef} tabIndex={-1} className="mt-1.5 text-lg font-semibold leading-snug text-fg focus:outline-none">
          <Highlight text={model.titleText} highlighter={highlighter} />
        </h2>
        {model.aliases.length > 0 && (
          <p className="mt-1 line-clamp-2 text-sm text-muted">
            Also: <Highlight text={model.aliases.join(', ')} highlighter={highlighter} />
          </p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {model.link && (
            <a
              href={model.link}
              target="_blank"
              rel="noopener noreferrer"
              referrerPolicy="no-referrer"
              className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-accent-solid px-3 text-xs font-medium text-on-accent hover:bg-accent-solid/90"
            >
              Open on lab website <ArrowUpRight size={14} aria-hidden="true" />
              <span className="sr-only">(new tab)</span>
            </a>
          )}
          {model.homepage && model.homepage !== model.link && (
            <a
              href={model.homepage}
              target="_blank"
              rel="noopener noreferrer"
              referrerPolicy="no-referrer"
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-line-strong bg-sunken px-3 text-xs font-medium text-fg-2 hover:bg-raised hover:text-fg"
            >
              <BookOpen size={14} aria-hidden="true" /> {model.labName} handbook
              <span className="sr-only">(new tab)</span>
            </a>
          )}
          {model.referral === 'referred' && <Badge tone="accent">Referred out</Badge>}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin" data-detail-scroll>
        {facts.length > 0 && (
          <section aria-label="Key facts" className="px-5 py-4">
            <dl className="grid gap-2 sm:grid-cols-2">
              {facts.map(([key, label]) => (
                <div
                  key={key}
                  className={cx(
                    'rounded-lg border border-line bg-sunken/60 px-3 py-2',
                    (key === 'stability' || key === 'transport' || key === 'performedAt' || key === 'referredTo') && 'sm:col-span-2',
                  )}
                >
                  <dt className="text-xs text-muted">{label}</dt>
                  <dd className="mt-0.5 text-sm text-fg">
                    <Highlight text={model.facts[key]} highlighter={highlighter} />
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {loading && (
          <div className="flex items-center gap-2 border-t border-line px-5 py-6 text-sm text-muted">
            <Spinner /> Loading full details from {model.labName}…
          </div>
        )}
        {error && (
          <div className="border-t border-line px-5 py-4">
            <Notice
              tone="danger"
              icon={AlertTriangle}
              title="Couldn't load the full details"
              action={<Button size="sm" onClick={retry}>Try again</Button>}
            >
              {error}
            </Notice>
          </div>
        )}
        {!loading && !error && sections.map(({ key, label, data }) => (
          <Section key={key} title={label} data={data} highlighter={highlighter} />
        ))}
        {!loading && !error && !sections.length && !facts.length && (
          <p className="border-t border-line px-5 py-6 text-sm text-muted">
            This lab doesn't provide details here.
            {model.link && ' Open the lab website for full information.'}
          </p>
        )}
        {model.link && (
          <p className="flex items-center gap-1.5 border-t border-line px-5 py-4 text-xs text-muted">
            <Link2 size={13} aria-hidden="true" />
            <span className="truncate">{model.link}</span>
          </p>
        )}
      </div>
    </article>
  );
};
